import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { RadioBtnInventory } from '@inventory/models/radio-btn-inventory.model';
import { TranslateService } from '@ngx-translate/core';
import { PlatformEnum, PlatformService } from '@panamax/app-state';
import { BaseDualPaneModalContainerComponent } from '@shared/components/modal-with-nav-inline/dual-pane-container/base-dual-pane-modal-container/base-dual-pane-modal-container.component';
import {
  dualPaneDefaultVerifySave,
  dualPaneMultiSelection,
  getSearchableObservable$,
  inventoryMultiSaveOnNavigation,
  inventoryRadioMatch,
  multiInventorySelection,
  multiSelectInventoryVerifySave,
  onMultiInputDefault,
  onMultiRangeDefault,
  onSmallChangeCurrencyCleanp,
  radioBtnInventorySearchDefault,
  rangeCleanupZeroToHundred,
  singleSelectRadioSearchDefault,
  updateRightPaneWithValue,
  updateRightPaneWithValueForMaps,
} from '@shared/components/modal-with-nav-inline/dual-pane-container/Helpers/dual-pane-helper-functions';
import {
  DualPaneView,
  MultiInputItems,
  MultiSelectItems,
} from '@shared/constants/dual-pane.enum';
import {
  areDatesEqual,
  formatDateForApiRequests,
  formatDateToMMDDYYYY,
  formatDateToMonthSlashDaySlashYear,
  isDateGreater,
} from '@shared/helpers/calendar.helpers';
import {
  BasicListItem,
  DualPaneFlagsConfig,
  DualPaneFunctionsConfig,
  DualPaneLabelsConfig,
  DualPaneViewModel,
  InputLabelItems,
  LeftPaneInfo,
  RightPaneInfo,
} from '@shared/models/dual-pane-model';
import { CurrencyService } from '@shared/services/currency.service';
import { BehaviorSubject, map, Observable, of } from 'rxjs';
import { FoodCostTotalData } from '../../models/food-cost-view-model.model';

@Component({
  selector: 'app-food-cost-modal-container',
  templateUrl: './food-cost-modal-container.component.html',
  styleUrls: ['./food-cost-modal-container.component.scss'],
})
export class FoodCostModalContainerComponent extends BaseDualPaneModalContainerComponent {
  @Input() translateService: TranslateService;
  @Input() datePipe: DatePipe;
  @Input() inventories: Observable<RadioBtnInventory[]>;
  @Input() foodCostRangeExists = false;

  @Output() foodPurchaseDataRequest: EventEmitter<FoodCostTotalData> =
    new EventEmitter(undefined);
  lastDataSetSent: FoodCostTotalData;
  foodCostPurchaseTotal: number;
  FoodPurchaseItemToUpdate: InputLabelItems = undefined;
  foodPurchaseIconToUpdate: InputLabelItems = undefined;

  constructor(public platformService: PlatformService) {
    super(platformService);
  }

  createFoodCostVM = () => {
    const labelConfig: DualPaneLabelsConfig = {
      dataCy: 'food-cost-report-modal',
      title: 'i18n.inventory.foodCost.modal.title',
      subTitle:
        this.platformService.platformType ===
        this.platformService.platformEnum.tablet
          ? 'i18n.inventory.foodCost.modal.subTitleTablet'
          : 'i18n.inventory.foodCost.modal.subTitle',
      submitText: this.translateService.instant('i18n.common.submit'),
      backText: this.translateService.instant('i18n.common.apply'),
    };
    const functionConfig: DualPaneFunctionsConfig = {
      intialDesktopTabletView: this.buildFirstSetOfInventoryRightPane,
      handleSubmit: this.submitModalValues,
      verifySubmit: this.foodCostVerifySubmit,
      outsideLifecycleOperation: this.updatePurchaseTotalOutsideOperation,
    };

    const flagConfig: DualPaneFlagsConfig = {};

    const contentItems = [
      {
        label: 'i18n.inventory.foodCost.modal.beginInventory',
        placeHolder: 'i18n.inventory.foodCost.modal.pleaseSelect',
        view: DualPaneView.multiSelectInventory,
        selected: this.platformService.platformType !== PlatformEnum.mobile,
        onSelectfunction: this.buildFirstSetOfInventoryRightPane,
      },
      {
        label: 'i18n.inventory.foodCost.modal.endInventory',
        placeHolder: 'i18n.inventory.foodCost.modal.pleaseSelect',
        view: DualPaneView.multiSelectInventory,
        selected: false,
        onSelectfunction: this.buildSecondSetOfInventoryRightPane,
      },
      {
        label: 'i18n.inventory.foodCost.modal.excludeLabel',
        placeHolder: 'i18n.inventory.foodCost.modal.excludePlaceholder',
        view: DualPaneView.searchableItemRadio,
        selected: false,
        onSelectfunction: this.buildExcludeItems,
      },
      {
        label: 'i18n.inventory.foodCost.modal.foodPurchase',
        placeHolder: 'i18n.inventory.foodCost.modal.foodPurchasePlaceholder',
        view: DualPaneView.multiInput,
        selected: false,
        onSelectfunction: this.buildFoodPurchaseRightPane,
      },
      {
        label: 'i18n.inventory.foodCost.modal.salesRevenue',
        placeHolder: 'i18n.inventory.foodCost.modal.salesPlaceholder',
        view: DualPaneView.multiInput,
        selected: false,
        onSelectfunction: this.buildSalesRevenueRightPane,
      },
    ] as BasicListItem[];

    if (!this.foodCostRangeExists) {
      contentItems.push({
        label: 'i18n.inventory.foodCost.modal.targetFoodCost',
        placeHolder: 'i18n.inventory.foodCost.modal.targetFoodCostPlaceholder',
        view: DualPaneView.multiInput,
        selected: false,
        onSelectfunction: this.buildFoodCostRangeRightPane,
      });
    }

    this.buildBasePaneWithConfigs(
      labelConfig,
      functionConfig,
      flagConfig,
      contentItems,
    );

    this.lastDataSetSent = undefined;
    this.FoodPurchaseItemToUpdate = undefined;
    this.foodPurchaseIconToUpdate = undefined;
    this.foodCostPurchaseTotal = undefined;
  };

  // Left pane index: 0
  buildFirstSetOfInventoryRightPane = (
    viewModel: DualPaneViewModel,
    paneSelected: string,
  ) => {
    const valueArray = viewModel?.leftView?.content[0].value ?? [];
    const searchableInventoryModal = {
      title: 'i18n.inventory.foodCost.modal.beginInventory',
      view: DualPaneView.multiSelectInventory,
      itemsObservable$: this.inventories.pipe(
        map(inventories => {
          return inventories?.map(inventory => {
            return {
              ...inventory,
              selected: valueArray.find(val => {
                return inventoryRadioMatch(val, inventory);
              }),
            };
          });
        }),
      ),
      errorMessage: 'i18n.inventory.foodCost.modal.errorMessage',
      error: this.getErrorForSecondSet(),
      searchPane: {
        searchPlaceholder: 'i18n.inventory.searchForInventory',
        onSearchFunction: radioBtnInventorySearchDefault,
        searchBehavior: new BehaviorSubject<string>(''),
        searchKey: '',
      },
      handleItemSelection: this.beginningInventorySelection,
      verifySaveButton: multiSelectInventoryVerifySave,
      overrideSaveOnNavigation: this.beginningInventorySaveOnNavigation,
    } as RightPaneInfo;

    searchableInventoryModal.searchPane.searchableObservable$ =
      getSearchableObservable$(searchableInventoryModal);
    updateRightPaneWithValue(viewModel, paneSelected, searchableInventoryModal);

    return searchableInventoryModal;
  };

  beginningInventorySelection = (
    left: LeftPaneInfo,
    platform: PlatformEnum,
    right: RightPaneInfo,
    value: any,
  ) => {
    let tempArray = [...(right.value ?? [])] as any[];
    if (value?.selected) {
      tempArray.push(value);
    } else {
      const index = tempArray.findIndex(invCheck => {
        return inventoryRadioMatch(invCheck, value);
      });
      tempArray = tempArray.filter((val, i) => {
        return i !== index;
      });
    }
    right.value = tempArray;

    let subLabel = undefined;
    if (tempArray.length === 1) {
      subLabel = tempArray[0].inventoryName;
    } else if (tempArray.length > 1) {
      subLabel =
        this.translateService.instant('i18n.lists.copyListModal.selected') +
        ' ' +
        tempArray.length;
    }

    if (platform !== PlatformEnum.mobile) {
      this.adjustSecondPane(left, right?.value ?? []);
    }

    left.content = left.content.map(item => {
      if (item.selected) {
        return {
          ...item,
          subLabel,
          value: tempArray,
        };
      } else {
        return item;
      }
    });
    this.clearFoodPurchaseDataAndUpdateLabel();
  };

  beginningInventorySaveOnNavigation = (
    vm: DualPaneViewModel,
    item: BasicListItem,
    value: any,
  ) => {
    let subLabel = undefined;
    if (value.length === 1) {
      subLabel = value[0].inventoryName;
    } else if (value.length > 1) {
      subLabel =
        this.translateService.instant('i18n.lists.copyListModal.selected') +
        ' ' +
        value.length;
    }
    item.value = value;
    item.subLabel = subLabel;
    this.adjustSecondPane(vm.leftView, value ?? []);
    return item;
  };

  adjustSecondPane = (left: LeftPaneInfo, value: any[]) => {
    // Adjust End Inventory Pane value and sublabel
    const endInventoryPane: BasicListItem = left.content[1];
    const latestDate = this.getLatestInventoryDate(value);
    const departments = this.getDepartmentsFromInventories(value);
    const updatedEndInventoryPaneValue: RadioBtnInventory[] = [];

    endInventoryPane?.value?.forEach(
      (selectedEndInventory: RadioBtnInventory) => {
        if (
          isDateGreater(selectedEndInventory.inventoryDate, latestDate) &&
          this.isInventoryInDepartments(departments, selectedEndInventory)
        ) {
          updatedEndInventoryPaneValue.push(selectedEndInventory);
        }
      },
    );

    let endInventorySubLabel = 'i18n.inventory.foodCost.modal.pleaseSelect';
    if (updatedEndInventoryPaneValue.length === 1) {
      endInventorySubLabel = updatedEndInventoryPaneValue[0].inventoryName;
    } else if (updatedEndInventoryPaneValue.length > 1) {
      endInventorySubLabel =
        this.translateService.instant('i18n.lists.copyListModal.selected') +
        ' ' +
        updatedEndInventoryPaneValue.length;
    }

    endInventoryPane.value = updatedEndInventoryPaneValue;
    endInventoryPane.subLabel = endInventorySubLabel;
  };

  // Left pane index: 1
  buildSecondSetOfInventoryRightPane = (
    viewModel: DualPaneViewModel,
    paneSelected: string,
  ) => {
    const contentArray = (viewModel?.leftView?.content[0].value ?? []) as any[];
    const latestDate = this.getLatestInventoryDate(contentArray);
    const departments = this.getDepartmentsFromInventories(contentArray);
    const valueArray = viewModel?.leftView?.content[1].value ?? [];
    const itemsObservable$ = this.inventories.pipe(
      map(inventories => {
        const filteredInventories = inventories.filter(inv => {
          return (
            isDateGreater(inv.inventoryDate, latestDate) &&
            this.isInventoryInDepartments(departments, inv)
          );
        });
        return filteredInventories?.map(inventory => {
          return {
            ...inventory,
            selected: valueArray.find(val => {
              return inventoryRadioMatch(val, inventory);
            }),
          };
        });
      }),
    );
    const searchableInventoryModal = {
      title: 'i18n.inventory.foodCost.modal.endInventory',
      view: DualPaneView.multiSelectInventory,
      itemsObservable$,
      errorMessage: 'i18n.inventory.foodCost.modal.errorMessage',
      error: this.getErrorForSecondSet(),
      searchPane: {
        searchPlaceholder: 'i18n.inventory.searchForInventory',
        onSearchFunction: radioBtnInventorySearchDefault,
        searchBehavior: new BehaviorSubject<string>(''),
        searchKey: '',
      },
      handleItemSelection: this.endInventorySelection,
      verifySaveButton: multiSelectInventoryVerifySave,
      overrideSaveOnNavigation: inventoryMultiSaveOnNavigation,
    } as RightPaneInfo;

    searchableInventoryModal.searchPane.searchableObservable$ =
      getSearchableObservable$(searchableInventoryModal);
    updateRightPaneWithValue(viewModel, paneSelected, searchableInventoryModal);

    return searchableInventoryModal;
  };

  endInventorySelection = (
    left: LeftPaneInfo,
    platform: PlatformEnum,
    right: RightPaneInfo,
    value: any,
  ) => {
    this.clearFoodPurchaseDataAndUpdateLabel();
    return multiInventorySelection(left, platform, right, value);
  };

  // Left pane index: 2
  buildExcludeItems = (viewModel: DualPaneViewModel, paneSelected: string) => {
    const valueArray: any[] = viewModel?.leftView?.content[2].value ?? [];
    const excludeModal = {
      view: DualPaneView.multiSelectCheckbox,
      title: 'i18n.inventory.foodCost.modal.excludeLabel',
      verifySaveButton: (vm: any) => true,
      searchPane: {
        hideSearch: true,
        onSearchFunction: singleSelectRadioSearchDefault,
        searchBehavior: new BehaviorSubject<string>(''),
        searchKey: '',
        searchPlaceholder: '',
        searchTitle: '',
      },
      countPane: {
        primaryCount: 0,
        hideSelectAll: true,
        selectableText: '',
      },
      itemsObservable$: of([
        {
          label: 'i18n.inventory.foodCost.modal.class19',
          value: '19',
          selected: valueArray.find(item => item.value === '19'),
          type: MultiSelectItems.basic,
        },
        {
          label: 'i18n.inventory.foodCost.modal.class23',
          value: '23',
          selected: valueArray.find(item => item.value === '23'),
          type: MultiSelectItems.basic,
        },
        {
          label: 'i18n.inventory.foodCost.modal.class24',
          value: '24',
          selected: valueArray.find(item => item.value === '24'),
          type: MultiSelectItems.basic,
        },
        {
          label: 'i18n.inventory.foodCost.modal.class36',
          value: '36',
          selected: valueArray.find(item => item.value === '36'),
          type: MultiSelectItems.basic,
        },
      ]),
      handleItemSelection: this.excludeClassSelection,
      overrideSaveOnNavigation: this.saveOverrideExclude,
    } as RightPaneInfo;
    excludeModal.searchPane.searchableObservable$ =
      getSearchableObservable$(excludeModal);
    updateRightPaneWithValue(viewModel, paneSelected, excludeModal);
    return excludeModal;
  };

  saveOverrideExclude = (
    vm: DualPaneViewModel,
    item: BasicListItem,
    value: any,
  ) => {
    item.value = value;
    item.subLabel =
      value.length > 0
        ? this.translateService.instant('i18n.lists.massDeleteModal.selected') +
          value.length
        : '';
    return item;
  };

  excludeClassSelection = (
    left: LeftPaneInfo,
    platform: PlatformEnum,
    right: RightPaneInfo,
    value: any,
    selectedCount: string,
  ) => {
    this.clearFoodPurchaseDataAndUpdateLabel();
    left.content[2].subLabel = value.length > 0 ? selectedCount : '';
    left.content[2].value = value;
    right.value = value;
  };

  // Left pane index: 3
  buildFoodPurchaseRightPane = (
    viewModel: DualPaneViewModel,
    paneSelected: string,
  ) => {
    let input1 = undefined;
    const inputMap = viewModel?.leftView?.content[3].value;
    if (!!inputMap) {
      input1 = inputMap.get(3);
    }

    let dataNeedsLoading = false;
    if (
      (viewModel.leftView.content[0].value ?? []).length > 0 &&
      (viewModel.leftView.content[1].value ?? []).length > 0
    ) {
      // gather current data
      const departmentNumbers = [];
      const inventories = [
        ...(viewModel.leftView.content[0].value ?? []),
        ...(viewModel.leftView.content[1].value ?? []),
      ];
      inventories.forEach(inventory => {
        departmentNumbers.push(inventory.departmentNumber);
      });
      const startDate = formatDateForApiRequests(
        this.getEarliestInventoryDate(
          viewModel.leftView.content[0].value ?? [],
        ),
      );
      const endDate = formatDateForApiRequests(
        this.getLatestInventoryDate(viewModel.leftView.content[1].value ?? []),
      );
      const departmentString = departmentNumbers.join(',');

      // see if old data matches new
      if (this.lastDataSetSent?.departmentNumbers !== departmentString)
        dataNeedsLoading = true;
      if (this.lastDataSetSent?.inventoryStartDate !== startDate)
        dataNeedsLoading = true;
      if (this.lastDataSetSent?.inventoryEndDate !== endDate)
        dataNeedsLoading = true;

      // send data request if needed
      if (dataNeedsLoading) {
        let exclusions = '';
        (viewModel.leftView.content[2]?.value ?? []).forEach(exlude => {
          if (exclusions === '') {
            exclusions += exlude.value;
          } else {
            exclusions += ',' + exlude.value;
          }
        });
        this.lastDataSetSent = {
          departmentNumbers: departmentString,
          inventoryStartDate: startDate,
          inventoryEndDate: endDate,
          excludeClass: exclusions,
        };
        this.foodCostPurchaseTotal = undefined;
        this.foodPurchaseDataRequest.next(this.lastDataSetSent);
      }
    }

    const foodPurchaseItem = {
      type: MultiInputItems.label,
      label: 'i18n.inventory.foodCost.modal.foodPurchaseInputLabel',
      subLabel: CurrencyService.toUSDString(this.foodCostPurchaseTotal ?? 0),
      isLoading: this.foodCostPurchaseTotal === undefined || dataNeedsLoading,
      smallMargin: true,
    };

    const foodPurchaseIcon = {
      type:
        this.foodCostPurchaseTotal === undefined || dataNeedsLoading
          ? MultiInputItems.blank
          : MultiInputItems.iconLabel,
      label: 'i18n.inventory.foodCost.modal.foodPurchaseIconLabel',
      subLabel: 'information-circle-outline',
    };

    this.foodPurchaseIconToUpdate = foodPurchaseIcon;

    this.FoodPurchaseItemToUpdate = foodPurchaseItem;

    const multiInput = {
      title: 'i18n.inventory.foodCost.modal.foodPurchase',
      view: DualPaneView.multiInput,
      inputsPane: {
        items: [
          this.getPeriodLabel(viewModel),
          foodPurchaseItem,
          foodPurchaseIcon,
          {
            type: MultiInputItems.negativeCurrency,
            label: 'i18n.inventory.foodCost.modal.otherPurchaseLabel',
            placeHolder1:
              'i18n.inventory.foodCost.modal.otherPurchasePlaceholder',
            underline: false,
            onInput: onMultiInputDefault,
            inputcleanup: onSmallChangeCurrencyCleanp,
            maxDecimal: 7,
            maxPrecision: 2,
            inputValue: input1,
          },
        ],
        handleValueUpdate: this.handleFoodPurchase,
      },
      verifySaveButton: this.verifyFoodPuchase,
      overrideSaveOnNavigation: this.saveOverrideFoodPurchase,
    } as RightPaneInfo;

    updateRightPaneWithValueForMaps(viewModel, paneSelected, multiInput);
    return multiInput;
  };

  verifyFoodPuchase = (vm: DualPaneViewModel) => {
    return this.foodCostPurchaseTotal !== undefined;
  };

  getFoodPuchaseSubLabel = (value: any) => {
    let subLabel = '';
    if (this.foodCostPurchaseTotal !== undefined) {
      subLabel += CurrencyService.toUSDString(this.foodCostPurchaseTotal);
    } else {
      subLabel += '$0.00';
    }

    const otherFoodInput = value;
    if (!!otherFoodInput) {
      subLabel += ', ' + CurrencyService.toUSDString(otherFoodInput);
    } else {
      subLabel += this.translateService.instant(', $0.00');
    }
    return subLabel;
  };

  saveOverrideFoodPurchase = (
    vm: DualPaneViewModel,
    item: BasicListItem,
    value: any,
  ) => {
    item.value = value;
    item.subLabel = this.getFoodPuchaseSubLabel(value?.get(3));
    return item;
  };

  handleFoodPurchase = (
    left: LeftPaneInfo,
    right: RightPaneInfo,
    value: any,
    index: number,
  ) => {
    if (!right.value) {
      right.value = new Map<number, any>();
    }
    right.value.set(index, value);

    left.content = left.content.map(item => {
      if (item.selected) {
        return {
          ...item,
          subLabel: this.getFoodPuchaseSubLabel(value),
          value: right.value,
        };
      } else {
        return item;
      }
    });
  };

  // Left pane index: 4
  buildSalesRevenueRightPane = (
    viewModel: DualPaneViewModel,
    paneSelected: string,
  ) => {
    let inputValue = undefined;
    const inputMap = viewModel?.leftView?.content[4].value;
    if (!!inputMap) {
      inputValue = inputMap.get(1);
    }
    const multiInput = {
      title: 'i18n.inventory.foodCost.modal.salesRevenue',
      view: DualPaneView.multiInput,
      inputsPane: {
        items: [
          this.getPeriodLabel(viewModel),
          {
            type: MultiInputItems.currencyInput,
            label: 'i18n.inventory.foodCost.modal.salesRevenue',
            placeHolder1: 'i18n.inventory.foodCost.modal.salesPlaceholder',
            underline: false,
            maxDecimal: 7,
            maxPrecision: 2,
            onInput: onMultiInputDefault,
            inputcleanup: onSmallChangeCurrencyCleanp,
            inputValue,
          },
        ],
        handleValueUpdate: this.handleSalesRevenue,
      },
      verifySaveButton: this.verifySales,
      overrideSaveOnNavigation: this.saveOverrideSalesRevenue,
    } as RightPaneInfo;

    updateRightPaneWithValueForMaps(viewModel, paneSelected, multiInput);
    return multiInput;
  };

  verifySales = (vm: DualPaneViewModel) => {
    return !!vm?.rightView.value?.get(1);
  };

  saveOverrideSalesRevenue = (
    vm: DualPaneViewModel,
    item: BasicListItem,
    value: any,
  ) => {
    let subLabel = undefined;
    const currency = value?.get(1);
    if (!!currency) {
      subLabel = CurrencyService.toUSDString(currency);
    }
    item.value = value;
    item.subLabel = subLabel;
    return item;
  };

  handleSalesRevenue = (
    left: LeftPaneInfo,
    right: RightPaneInfo,
    value: any,
    index: number,
  ) => {
    if (!right.value) {
      right.value = new Map<number, any>();
    }
    right.value.set(index, value);
    let subLabel = '';
    if (!!value) {
      subLabel = CurrencyService.toUSDString(value);
    }
    left.content = left.content.map(item => {
      if (item.selected) {
        return { ...item, subLabel, value: right.value };
      } else {
        return item;
      }
    });
  };

  // Left pane index: 5
  buildFoodCostRangeRightPane = (
    viewModel: DualPaneViewModel,
    paneSelected: string,
  ) => {
    let rangeValueOne = undefined;
    let rangeValueTwo = undefined;
    const inputMap = viewModel?.leftView?.content[5].value;
    if (!!inputMap) {
      rangeValueOne = inputMap.get(0)?.one;
      rangeValueTwo = inputMap.get(0)?.two;
    }
    const multiInput = {
      title: 'i18n.inventory.foodCost.modal.targetFoodCost',
      view: DualPaneView.multiInput,
      inputsPane: {
        items: [
          {
            type: MultiInputItems.percentageRange,
            label: 'i18n.inventory.foodCost.modal.targetFoodCost',
            placeHolder1: 'i18n.inventory.foodCost.modal.1%',
            placeHolder2: 'i18n.inventory.foodCost.modal.100%',
            rangeLabelIsArray: false,
            inputcleanup: rangeCleanupZeroToHundred,
            onRange: onMultiRangeDefault,
            rangeValueOne,
            rangeValueTwo,
          },
        ],
        handleValueUpdate: this.handleFoodCost,
      },
      verifySaveButton: this.verifyFoodCost,
      overrideSaveOnNavigation: this.saveOverrideFoodCost,
    } as RightPaneInfo;

    updateRightPaneWithValueForMaps(viewModel, paneSelected, multiInput);
    return multiInput;
  };

  verifyFoodCost = (vm: DualPaneViewModel) => {
    return (
      !!vm?.rightView.value?.get(0)?.one && !!vm?.rightView.value?.get(0)?.two
    );
  };

  saveOverrideFoodCost = (
    vm: DualPaneViewModel,
    item: BasicListItem,
    value: any,
  ) => {
    let subLabel = '';
    const range = value?.get(0);

    if (!!range?.one) {
      subLabel += value?.get(0)?.one + '%';
    } else {
      subLabel += '?%';
    }

    if (range?.two) {
      subLabel += ' - ' + value?.get(0)?.two + '%';
    } else {
      subLabel += ' - ?%';
    }

    item.value = value;
    item.subLabel = subLabel;
    return item;
  };

  handleFoodCost = (
    left: LeftPaneInfo,
    right: RightPaneInfo,
    value: any,
    index: number,
  ) => {
    if (!right.value) {
      right.value = new Map<number, any>();
    }
    right.value.set(index, value);
    let subLabel = '';
    if (!!value?.one || !!value?.two) {
      if (!!value?.one) {
        subLabel += value.one + '%';
      } else {
        subLabel += '?%';
      }

      if (!!value?.two) {
        subLabel += ' - ' + value.two + '%';
      } else {
        subLabel += ' - ?%';
      }
    }

    left.content = left.content.map(item => {
      if (item.selected) {
        return { ...item, subLabel, value: right.value };
      } else {
        return item;
      }
    });
  };

  getLatestInventoryDate = (inventories: RadioBtnInventory[]) => {
    let dateToReturn = new Date(+0);
    inventories.forEach(inventory => {
      const dateCompare = new Date(inventory.inventoryDate);
      if (isDateGreater(dateCompare, dateToReturn)) {
        dateToReturn = dateCompare;
      }
    });

    return dateToReturn;
  };

  isInventoryInDepartments = (depts: any[], inventory: RadioBtnInventory) => {
    if (depts.length === 0) {
      return true;
    } else {
      return depts.includes(inventory.departmentName);
    }
  };

  getDepartmentsFromInventories = (inventories: RadioBtnInventory[]) => {
    let depts = new Set();
    inventories.forEach(inventory => {
      depts.add(inventory.departmentName);
    });

    return Array.from(depts);
  };

  getErrorForSecondSet = () => {
    return this.inventories.pipe(
      map(inventories => {
        let datesAllMatch = true;
        let date;
        inventories?.forEach(inv => {
          if (!date) {
            date = inv.inventoryDate;
          } else {
            if (!areDatesEqual(inv.inventoryDate, date)) {
              datesAllMatch = false;
            }
          }
        });
        return inventories?.length <= 1 || datesAllMatch;
      }),
    );
  };

  getLatestDateLabel = (inventories: RadioBtnInventory[]) => {
    if (inventories.length === 0) {
      return '';
    } else {
      return formatDateToMonthSlashDaySlashYear(
        this.getLatestInventoryDate(inventories),
      );
    }
  };

  getEarliestDateLabel = (inventories: RadioBtnInventory[]) => {
    if (inventories.length === 0) {
      return '';
    } else {
      return formatDateToMonthSlashDaySlashYear(
        this.getEarliestInventoryDate(inventories),
      );
    }
  };

  getEarliestInventoryDate = (inventories: RadioBtnInventory[]) => {
    let dateToReturn = new Date();
    inventories.forEach(inventory => {
      const dateCompare = new Date(inventory.inventoryDate);
      if (isDateGreater(dateToReturn, dateCompare)) {
        dateToReturn = dateCompare;
      }
    });

    return dateToReturn;
  };

  checkInventoryDateName = (
    mapCheck: Map<string, string[]>,
    date: Date,
    name: string,
  ) => {
    const invDate = formatDateToMMDDYYYY(date);
    if (mapCheck.has(invDate)) {
      return mapCheck.get(invDate).includes(name);
    } else {
      return false;
    }
  };

  getPeriodLabel = (viewModel: DualPaneViewModel) => {
    const startRange = this.getEarliestDateLabel(
      viewModel?.leftView?.content[0]?.value ?? [],
    );
    const endRange = this.getLatestDateLabel(
      viewModel?.leftView?.content[1]?.value ?? [],
    );
    if (!!startRange && !!endRange) {
      return {
        type: MultiInputItems.rangeLabel,
        label: 'i18n.inventory.foodCost.modal.period',
        rangeValueOne: startRange,
        rangeValueTwo: endRange,
        rangeLabelIsArray: false,
      };
    } else {
      return {
        type: MultiInputItems.label,
        label: 'i18n.inventory.foodCost.modal.period',
        subLabel: 'i18n.inventory.foodCost.modal.pleaseSelect2',
      };
    }
  };

  // Need to clear food purchase total to avoid invalid report state with data
  clearFoodPurchaseDataAndUpdateLabel = () => {
    this.lastDataSetSent = undefined;
    this.foodCostPurchaseTotal = undefined;
    this.updateFoodPurchaseLeftPaneLabel(this.dualPaneModalData.viewModel);
  };

  updateFoodPurchaseLeftPaneLabel = (viewModel: DualPaneViewModel) => {
    const leftViewPane = viewModel.leftView.content[3];
    if (!!leftViewPane.value && leftViewPane.value.has(3)) {
      leftViewPane.subLabel = this.getFoodPuchaseSubLabel(
        leftViewPane.value.get(3),
      );
    } else {
      leftViewPane.subLabel = this.getFoodPuchaseSubLabel(undefined);
    }
  };

  updatePurchaseTotalOutsideOperation = (
    viewModel: DualPaneViewModel,
    data: any,
  ) => {
    viewModel.disableSubmit = !this.foodCostVerifySubmit(
      this.dualPaneModalData.viewModel,
    );
    if (
      this.platformService.platformType === PlatformEnum.mobile &&
      !!viewModel.rightView &&
      viewModel.rightView.title === 'i18n.inventory.foodCost.modal.foodPurchase'
    ) {
      viewModel.disableSave = !this.verifyFoodPuchase(viewModel);
    }
    this.updateFoodPurchaseLeftPaneLabel(viewModel);
    const foodCostNum = Number(data.total).valueOf();
    const FoodPurchaseItemToUpdate = data.itemRef;
    if (!!FoodPurchaseItemToUpdate) {
      FoodPurchaseItemToUpdate.subLabel =
        CurrencyService.toUSDString(foodCostNum);
      FoodPurchaseItemToUpdate.isLoading = false;
    }
  };

  updateFoodCostTotalFromOutsideContainer = foodCostPurchaseData => {
    if (typeof foodCostPurchaseData !== 'number') {
      this.closeModal();
    } else {
      this.foodCostPurchaseTotal = foodCostPurchaseData;
      this.outsideLifecycle$.next({
        total: foodCostPurchaseData,
        itemRef: this.FoodPurchaseItemToUpdate,
      });
      if (!!this.foodPurchaseIconToUpdate)
        this.foodPurchaseIconToUpdate.type = MultiInputItems.iconLabel;
    }
  };

  foodCostVerifySubmit = (vm: DualPaneViewModel) => {
    const beginInventoryIsInvalid =
      !vm.leftView.content[0]?.value ||
      vm.leftView.content[0]?.value.length === 0;
    const endInventoryIsInvalid =
      !vm.leftView.content[1]?.value ||
      vm.leftView.content[1]?.value.length === 0;
    const salesRevenueIsInvalid =
      !vm.leftView.content[4]?.value || !vm.leftView.content[4]?.value?.get(1);
    const foodPurchaseTotalIsInvalid = this.foodCostPurchaseTotal === undefined;
    const foodCostRangeDoesNotExistAndIsInvalid =
      !this.foodCostRangeExists &&
      (!vm.leftView.content[5]?.value ||
        !vm.leftView.content[5]?.value?.get(0)?.one ||
        !vm.leftView.content[5]?.value?.get(0)?.two);

    // cheeck that no required input is invalid
    return !(
      beginInventoryIsInvalid ||
      endInventoryIsInvalid ||
      salesRevenueIsInvalid ||
      foodPurchaseTotalIsInvalid ||
      foodCostRangeDoesNotExistAndIsInvalid
    );
  };

  submitModalValues = async (vm: DualPaneViewModel) => {
    const submitMap = new Map<string, any | any[]>();
    submitMap.set('Begin Inventories', vm.leftView.content[0]?.value);
    submitMap.set('End Inventories', vm.leftView.content[1]?.value);
    submitMap.set('Food Purchases', vm.leftView.content[3]?.value);
    submitMap.set('Purchase Total', this.foodCostPurchaseTotal);
    submitMap.set('Sales Revenues', vm.leftView.content[4]?.value);
    if (!this.foodCostRangeExists)
      submitMap.set('Food Cost Range', vm.leftView.content[5]?.value);

    submitMap.set(
      'Begin Inventory Date',
      this.getEarliestInventoryDate(
        vm?.leftView?.content[0]?.value ?? [],
      ).toISOString(),
    );
    submitMap.set(
      'End Inventory Date',
      this.getLatestInventoryDate(
        vm?.leftView?.content[1]?.value ?? [],
      ).toISOString(),
    );

    this.submit$.next(submitMap);
    this.closeModalAndSubmit();
  };

  async openModal() {
    await this.createFoodCostVM();
    this.isModalOpen$.next(true);
  }

  closeModal = () => {
    this.isModalOpen$.next(false);
  };

  closeModalAndSubmit() {
    this.isModalOpen$.next(false);
    if (this.submit$.value !== undefined) {
      this.submitEvent.emit(this.submit$.value);
    }
  }
}
